import { Alert, Checkbox, Form, type FormInstance, Input, Radio, Row, Tooltip } from "antd";
import { AlreadyRequestedLabel } from "molecules/PatientSummary/patientSummaryItems";
import { ColHalfWidth, FormItemGroup, RowTwoField } from "@vyne-shared/components/vyneForm/items/VyneFormItems";
import { CurrencyFormItem } from "@vyne-shared/components/Payment/Components/Amount/CurrencyFormItem";
import { InfoCircleOutlined } from "@ant-design/icons";
import { MessageArea } from "./MessageArea";
import { PatientSummaryFromPmsId } from "molecules/PatientSummary";
import { VyneInputRadio } from "@vyne-shared/components/vyneInputs/vyneInputRadio/VyneInputRadio";
import { VynePhoneInput } from "@vyne-shared/components/vynePhoneInput/VynePhoneInput";
import { balanceCheck, useDynamicValidation } from "./paymentForm.funcs";
import { stringFromBasisPoints } from "@vyne-shared/utils/currency";
import { validAmountNumber } from "@vyne-shared/components/Payment/Components/Amount/currencyInput";
import type { ExistingPatient } from "../existingPatient";
import type { PatientUpdateForm } from "api/requests";
import type { RequestChannel } from "@vyne-shared/api/VynePayments/responses";

interface FormItemsProps extends PhoneEmailAreaSpecificProps {
    readonly form: FormInstance<PatientUpdateForm>;
    readonly pmsIntegrated: boolean;
    readonly existingPrp: ExistingPatient;
    readonly hideBalance?: boolean;
    readonly hideMessaging?: boolean;
}

/** Build the items to include in the Payment Form. */
export const FormItems = ({
    form,
    pmsIntegrated,
    existingPrp,
    hideBalance,
    hideMessaging,
    ...phoneEmailProps
}: FormItemsProps) => (
    <>
        {pmsIntegrated ? <NameAreaPms existingPrp={existingPrp} /> : <NameArea />}
        {!hideBalance && (
            <FormItemGroup legendTitle="Amount">
                <BalanceArea existingPrp={existingPrp} />
            </FormItemGroup>
        )}
        <FormItemGroup legendTitle="Delivery Method">
            <PhoneEmailArea form={form} pmsOverridden={existingPrp?.pmsOverridden} {...phoneEmailProps} />
        </FormItemGroup>
        {!hideMessaging && <MessageArea form={form} existingPrp={existingPrp} />}
    </>
);

/** Build the area of the form inquiring about given name and family name. */
const NameArea = () => (
    <FormItemGroup legendTitle="Patient Info">
        <RowTwoField>
            <ColHalfWidth>
                <Form.Item
                    label="First Name"
                    name="givenName"
                    rules={[{ required: true, message: "First Name is required" }]}
                    required
                >
                    <Input required />
                </Form.Item>
            </ColHalfWidth>
            <ColHalfWidth>
                <Form.Item
                    label="Last Name"
                    name="familyName"
                    rules={[{ required: true, message: "Last Name is required" }]}
                    required
                >
                    <Input required />
                </Form.Item>
            </ColHalfWidth>
        </RowTwoField>
    </FormItemGroup>
);

/** Build the area of the form for patient name and summary when PMS-integrated. */
const NameAreaPms = ({ existingPrp }: { readonly existingPrp: ExistingPatient }) => (
    // existingPrp will always be present because creating new patients is not allowed when PMS-integrated
    // pmsId will always be present because we validate it when checking PMS-integrated flag

    // name is shown to the user space-concatenated, but submitted to API as separate parts
    // including name components as hidden items here passes UI validation without having to make it conditional
    <>
        <PatientSummaryFromPmsId
            pmsId={existingPrp!.pmsId!}
            paidWithoutWriteBackDate={existingPrp!.paidWithoutWriteBackDate}
            hideAlreadyRequested
        />
        <Form.Item hidden name="givenName">
            <Input readOnly />
        </Form.Item>
        <Form.Item hidden name="familyName">
            <Input readOnly />
        </Form.Item>
    </>
);

/** Build the area of the form asking for new balance, and showing any existing balance. */
const BalanceArea = ({ existingPrp }: { readonly existingPrp: ExistingPatient }) => (
    <RowTwoField>
        <ColHalfWidth>
            <CurrencyFormItem
                label="Requested Payment"
                rules={[
                    {
                        min: 0,
                        max: 100000,
                        validator: validAmountNumber,
                    },
                    {
                        validator: balanceCheck(existingPrp?.pmsBalances?.balanceEstimated),
                        warningOnly: true,
                    },
                ]}
                inputProps={{ placeholder: "10.00", required: true }}
            />
        </ColHalfWidth>
        <ColHalfWidth>
            <Form.Item label={<AlreadyRequestedLabel />} htmlFor="existingBalance" hidden={!existingPrp}>
                <Input
                    id="existingBalance"
                    readOnly
                    variant="borderless"
                    value={stringFromBasisPoints(existingPrp?.balance)}
                />
            </Form.Item>
        </ColHalfWidth>
    </RowTwoField>
);

const patientCommunicationDescription =
    "When disabled, Patients or Responsible Parties will not be automatically notified of new payment requests, and you will be prompted before manually sending notifications.";

interface PhoneEmailAreaProps extends PhoneEmailAreaSpecificProps {
    readonly form: FormInstance<PatientUpdateForm>;
    readonly pmsOverridden?: boolean;
}

interface PhoneEmailAreaSpecificProps {
    readonly disableAllowComm?: boolean;
    readonly initialPhone: string | undefined;
    readonly initialEmail: string | undefined;
    readonly initialRequestChannel: RequestChannel;
}

/** Build the area of the form inquiring about contact details. */
const PhoneEmailArea = ({
    form,
    pmsOverridden,
    disableAllowComm,
    initialPhone,
    initialEmail,
    initialRequestChannel,
}: PhoneEmailAreaProps) => {
    const { phone, email } = useDynamicValidation(form, initialPhone, initialEmail, initialRequestChannel);

    return (
        <>
            {!disableAllowComm && (
                <Form.Item name="allowCommunication" valuePropName="checked">
                    <Checkbox>
                        Allow automatic communication{" "}
                        <Tooltip title={patientCommunicationDescription}>
                            <InfoCircleOutlined />
                        </Tooltip>
                    </Checkbox>
                </Form.Item>
            )}
            <Form.Item name="requestChannel">
                {/* block makes the radio group take entire width (vs. default inline-block) */}
                <Radio.Group style={{ display: "block" }}>
                    <Row>
                        <VyneInputRadio value="Both">Both text &amp; email</VyneInputRadio>
                    </Row>
                    <RowTwoField>
                        <ColHalfWidth>
                            <VyneInputRadio value="SMS">Text</VyneInputRadio>
                        </ColHalfWidth>
                        <ColHalfWidth>
                            <VyneInputRadio value="Email">Email</VyneInputRadio>
                        </ColHalfWidth>
                    </RowTwoField>
                </Radio.Group>
            </Form.Item>
            <RowTwoField>
                <ColHalfWidth>
                    <Form.Item
                        label="Phone"
                        name="phone"
                        dependencies={["requestChannel"]}
                        validateFirst
                        rules={phone}
                        required={phone[0].required}
                    >
                        <VynePhoneInput required={phone[0].required} initialPhone={initialPhone ?? ""} />
                    </Form.Item>
                </ColHalfWidth>
                <ColHalfWidth>
                    <Form.Item
                        label="Email"
                        name="email"
                        dependencies={["requestChannel"]}
                        validateFirst
                        rules={email}
                        required={email[0].required}
                    >
                        <Input type="email" required={email[0].required} />
                    </Form.Item>
                </ColHalfWidth>
            </RowTwoField>
            <PmsOverriddenTip pmsOverridden={pmsOverridden} />
        </>
    );
};

const PmsOverriddenTip = ({ pmsOverridden }: { readonly pmsOverridden?: boolean }) => {
    let msg = "";
    let type: React.ComponentProps<typeof Alert>["type"] = "info";

    switch (pmsOverridden) {
        case false:
            msg =
                "Changes made to patient information will remain until new information is entered into your practice management system.";
            break;
        case true:
            msg =
                "Patient Information has been changed from what was supplied by your practice software, and will be used for future requests, until updated information is synced.";
            type = "warning";
            break;
    }

    return msg ? <Alert type={type} message={msg} /> : null;
};
